import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, SlidersHorizontal } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PageError } from '@/components/ui/PageAsyncState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { ContextMenu, ContextMenuRowButton } from '@/components/ui/ContextMenu'
import { MaterialPropertiesPanel } from '@/features/materials/MaterialPropertiesPanel'
import { computeMaterialListKpis } from '@/features/materials/materialListMetrics'
import {
  MATERIAL_KIND_LABEL,
  materialService,
  type Material,
  type MaterialKind,
  type MaterialStatus,
} from '@/services/materials'

const fieldClass =
  'h-8 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-accent)]'

export function MaterialListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Material[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [kindFilter, setKindFilter] = useState<'' | MaterialKind>('')
  const [statusFilter, setStatusFilter] = useState<'' | MaterialStatus>('')

  const [selectedMaterialIdForProps, setSelectedMaterialIdForProps] = useState<string | null>(null)
  const [isPropsPanelOpen, setIsPropsPanelOpen] = useState(false)
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean
    x: number
    y: number
    item: Material | null
  }>({
    isOpen: false,
    x: 0,
    y: 0,
    item: null,
  })

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      const list = await materialService.list()
      setRows(list)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được danh sách Material')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const kpis = useMemo(() => computeMaterialListKpis(rows), [rows])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (kindFilter && r.kind !== kindFilter) return false
      if (statusFilter && r.status !== statusFilter) return false
      return true
    })
  }, [rows, kindFilter, statusFilter])

  const columns: ProtoColumn<Material>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'Mã',
        sortable: true,
        width: 110,
        cell: (r) => <span className="font-mono text-slate-700">{r.code}</span>,
        sortValue: (r) => r.code,
      },
      {
        id: 'name',
        header: 'Tên vật tư',
        sortable: true,
        width: 240,
        cell: (r) => <span className="font-semibold text-[var(--color-link)]">{r.name}</span>,
        sortValue: (r) => `${r.name} ${r.code}`,
      },
      {
        id: 'quy_cach',
        header: 'Quy cách',
        sortable: true,
        width: 130,
        cell: (r) => r.attributes?.quy_cach || r.attributes?.spec || '—',
        sortValue: (r) => r.attributes?.quy_cach || r.attributes?.spec || '',
      },
      {
        id: 'mau',
        header: 'Màu',
        sortable: true,
        width: 100,
        cell: (r) => r.attributes?.mau || r.attributes?.color || '—',
        sortValue: (r) => r.attributes?.mau || r.attributes?.color || '',
      },
      {
        id: 'uom',
        header: 'ĐVT',
        cell: (r) => r.uom_default || '—',
        sortValue: (r) => r.uom_default ?? '',
      },
      {
        id: 'kind',
        header: 'Loại',
        sortable: true,
        cell: (r) => MATERIAL_KIND_LABEL[r.kind],
        sortValue: (r) => MATERIAL_KIND_LABEL[r.kind],
      },
      {
        id: 'status',
        header: 'Trạng thái',
        sortable: true,
        cell: (r) => (
          <StatusBadge
            tone={r.status === 'active' ? 'success' : 'neutral'}
            label={r.status === 'active' ? 'Đang dùng' : 'Ngừng dùng'}
            showDot={false}
          />
        ),
        sortValue: (r) => r.status,
      },
      {
        id: 'actions',
        header: 'Thao tác',
        width: 110,
        cell: (r) => (
          <ContextMenuRowButton
            onClick={() => {
              setSelectedMaterialIdForProps(r.id)
              setIsPropsPanelOpen(true)
            }}
          />
        ),
        sortable: false,
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Danh mục', to: '/catalog' },
          { label: 'Vật tư' },
        ]}
      />
      <PageHeader
        title="Vật tư (Material)"
        subtitle="Master vật tư mua được — Postgres SoT"
        actions={
          <Button onClick={() => navigate('/purchasing/materials/new')}>
            <Plus className="h-4 w-4" />
            Tạo vật tư
          </Button>
        }
      />

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Tổng" value={kpis.total} />
        <KpiCard label="Đang dùng" value={kpis.active} tone="success" />
        <KpiCard
          label="Ngừng dùng"
          value={kpis.inactive}
          tone={kpis.inactive ? 'warning' : 'default'}
        />
        <KpiCard label="RAW" value={kpis.raw} />
        <KpiCard label="AUX" value={kpis.aux} />
      </div>

      {error ? <PageError message={error} onRetry={() => void reload()} /> : null}

      <PageToolbar
        left={
          <>
            <select
              className={fieldClass}
              value={kindFilter}
              onChange={(e) =>
                setKindFilter(e.target.value as '' | MaterialKind)
              }
              aria-label="Lọc loại"
            >
              <option value="">Tất cả loại</option>
              <option value="RAW">{MATERIAL_KIND_LABEL.RAW}</option>
              <option value="AUX">{MATERIAL_KIND_LABEL.AUX}</option>
            </select>
            <select
              className={fieldClass}
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as '' | MaterialStatus)
              }
              aria-label="Lọc trạng thái"
            >
              <option value="">Tất cả trạng thái</option>
              <option value="active">Đang dùng</option>
              <option value="inactive">Ngừng dùng</option>
            </select>
          </>
        }
      />

      <ProtoExcelTable
        showRowNumbers
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        searchPlaceholder="Tìm tên vật tư..."
        exportName="vat-tu-material"
        pageSize={50}
        loading={loading}
        emptyMessage="Chưa có vật tư nào."
        stickyLeftColumnIds={['name']}
        onRowClick={(r) => navigate(`/purchasing/materials/${r.id}`)}
        onRowContextMenu={(r, e) => {
          setContextMenu({
            isOpen: true,
            x: e.clientX,
            y: e.clientY,
            item: r,
          })
        }}
      />

      <ContextMenu
        isOpen={contextMenu.isOpen}
        position={{ x: contextMenu.x, y: contextMenu.y }}
        onClose={() => setContextMenu((prev) => ({ ...prev, isOpen: false }))}
        items={[
          {
            id: 'properties',
            label: 'Thuộc tính vật tư...',
            icon: <SlidersHorizontal className="h-4 w-4" />,
            onClick: () => {
              if (contextMenu.item) {
                setSelectedMaterialIdForProps(contextMenu.item.id)
                setIsPropsPanelOpen(true)
              }
            },
          },
          {
            id: 'detail',
            label: 'Xem chi tiết vật tư',
            onClick: () => {
              if (contextMenu.item) {
                navigate(`/purchasing/materials/${contextMenu.item.id}`)
              }
            },
          },
        ]}
      />

      <MaterialPropertiesPanel
        isOpen={isPropsPanelOpen}
        materialId={selectedMaterialIdForProps}
        onClose={() => {
          setIsPropsPanelOpen(false)
          setSelectedMaterialIdForProps(null)
        }}
        onSaved={() => {
          void reload()
        }}
      />
    </div>
  )
}
